import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://<api-id>.execute-api.us-east-1.amazonaws.com/prod';

// ── Default values pre-filled from inputs-kg/ ────────────────────────────────
const DEFAULT_CONSTRAINTS = {
  general: [
    { name: 'ClampForceMinN',          label: 'Clamp Force Min',         value: 1200, unit: 'N'  },
    { name: 'ClampForceMaxN',          label: 'Clamp Force Max',         value: 5000, unit: 'N'  },
    { name: 'PartPositionToleranceMm', label: 'Part Position Tolerance', value: 2.0,  unit: 'mm' },
    { name: 'PartSeatToleranceM',      label: 'Part Seat Tolerance',     value: 0.05, unit: 'm'  },
  ],
  timing: [
    { event: 'FixtureCloseCommand', to_event: 'FixtureClosed',  max_duration_s: 5.0  },
    { event: 'RobotStart',          to_event: 'RobotAtHome',    max_duration_s: 5.0  },
    { event: 'PartLoaded',          to_event: 'CycleComplete',  max_duration_s: 20.0 },
    { event: 'FixtureOpenCommand',  to_event: 'FixtureOpened',  max_duration_s: 2.0  },
  ],
};

const DEFAULT_RULES = [
  { id: 'R_SEQ_001',  category: 'Sequence',   severity: 'Critical', description: 'Robot must start only after fixture is closed.' },
  { id: 'R_SEQ_002',  category: 'Sequence',   severity: 'Critical', description: 'Fixture may be considered closed only after both jaw-closed confirmations occur.' },
  { id: 'R_SEQ_003',  category: 'Sequence',   severity: 'Critical', description: 'Fixture must not open before robot returns home.' },
  { id: 'R_CAP_001',  category: 'Capability', severity: 'Major',    description: 'Clamp force must stay above minimum threshold.' },
  { id: 'R_SAFE_001', category: 'Safety',     severity: 'Critical', description: 'Nominal scenario must remain collision free.' },
  { id: 'R_SAFE_002', category: 'Safety',     severity: 'Critical', description: 'Critical collision severity must fail the run.' },
  { id: 'R_TIME_001', category: 'Timing',     severity: 'Major',    description: 'Fixture close duration shall remain within limit.' },
  { id: 'R_TIME_002', category: 'Timing',     severity: 'Major',    description: 'Cycle time shall remain within target.' },
  { id: 'R_PART_001', category: 'Part',       severity: 'Critical', description: 'Part must be detected as present before fixture close command is issued.' },
  { id: 'R_PART_002', category: 'Part',       severity: 'Critical', description: 'Part must remain clamped throughout robot operation.' },
  { id: 'R_PART_003', category: 'Part',       severity: 'Major',    description: 'Clamp force must not exceed maximum to prevent part deformation.' },
  { id: 'R_PART_004', category: 'Part',       severity: 'Major',    description: 'Part must be released cleanly after cycle completes.' },
  { id: 'R_PART_005', category: 'Part',       severity: 'Critical', description: 'Part must not collide with fixture during loading.' },
];

const DEFAULT_TM_IDS = [
  { device: 'BIW_Line',           entity: 'BIW_Line'           },
  { device: 'Robot_Cell_1',       entity: 'Robot_Cell_1'       },
  { device: 'Fixture_001',        entity: 'Fixture_001'        },
  { device: 'Robot_001',          entity: 'Robot_001'          },
  { device: 'CollisionAlert_001', entity: 'CollisionAlert_001' },
];

const DEFAULT_MODEL_FILES = [
  { device: 'L_Bracket', file: 'L_Bracket.fbx' },
];

const UNITY_SLOTS = [
  { key: 'scene',    label: 'BIW_RealVirtual_Scene.unity', hint: 'Assets/Scenes/',   ext: '.unity' },
  { key: 'exporter', label: 'BIW_EventExporter.cs',        hint: 'Assets/Scripts/',  ext: '.cs'    },
  { key: 'bridge',   label: 'BIW_AWS_Bridge.cs',           hint: 'Assets/Scripts/',  ext: '.cs'    },
];

const CATEGORIES  = ['Sequence', 'Capability', 'Safety', 'Timing', 'Part', 'FaultInjection'];
const SEVERITIES  = ['Critical', 'Major'];
const SEV_COLOR   = { Critical: '#c0392b', Major: '#e67e22' };
// ─────────────────────────────────────────────────────────────────────────────

export default function SubmitForm({ onJobCreated, session }) {
  const [version,     setVersion]     = useState('');
  const [baseline,    setBaseline]    = useState('');
  // email comes from authenticated session — never typed by engineer
  const email = session?.email || '';
  const [unityFiles,  setUnityFiles]  = useState({ scene: null, exporter: null, bridge: null });
  const [constraints, setConstraints] = useState(JSON.parse(JSON.stringify(DEFAULT_CONSTRAINTS)));
  const [rules,       setRules]       = useState(JSON.parse(JSON.stringify(DEFAULT_RULES)));
  const [tmIds,       setTmIds]       = useState(JSON.parse(JSON.stringify(DEFAULT_TM_IDS)));
  const [modelFiles,  setModelFiles]  = useState(JSON.parse(JSON.stringify(DEFAULT_MODEL_FILES)));
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState(null);
  const [section,     setSection]     = useState('unity');
  const [saveStatus,  setSaveStatus]  = useState({}); // { constraints: 'saved'|'saving'|'error', ... }

  // ── Unity file slots ────────────────────────────────────────────────────────
  function UnitySlot({ slotKey, label, hint, ext }) {
    const onDrop = useCallback((accepted) => {
      if (accepted.length) setUnityFiles(prev => ({ ...prev, [slotKey]: accepted[0] }));
    }, [slotKey]);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop, multiple: false, accept: { 'application/octet-stream': [ext] },
    });
    const file = unityFiles[slotKey];
    return (
      <div style={s.slotRow}>
        <div style={s.slotLabel}>
          <code style={s.code}>{label}</code>
          <small style={{ color: '#888', marginLeft: 8 }}>{hint}</small>
        </div>
        {file ? (
          <div style={s.slotFilled}>
            <span>✅ {file.name} <small>({(file.size/1024).toFixed(0)} KB)</small></span>
            <button onClick={() => setUnityFiles(p => ({ ...p, [slotKey]: null }))} style={s.removeBtn}>✕</button>
          </div>
        ) : (
          <div {...getRootProps()} style={{ ...s.miniDrop, borderColor: isDragActive ? '#0073bb' : '#aaa' }}>
            <input {...getInputProps()} />
            <span style={{ fontSize: 12, color: '#666' }}>Drop {ext} or click</span>
          </div>
        )}
      </div>
    );
  }

  // ── Build per-section JSON payload ─────────────────────────────────────────
  const buildPayload = (sec) => {
    if (sec === 'constraints') {
      const generalObj = Object.fromEntries(
        constraints.general.filter(g => g.name).map(g => [g.name, g.value])
      );
      return { ...generalObj, timing: constraints.timing };
    }
    if (sec === 'rules')
      return rules.map(({ id, category, severity, description }) => ({ id, category, severity, description }));
    if (sec === 'twinmaker')
      return Object.fromEntries(tmIds.filter(r => r.device).map(r => [r.device, r.entity]));
    if (sec === 'models')
      return Object.fromEntries(modelFiles.filter(r => r.device).map(r => [r.device, r.file]));
    return null;
  };

  const FILE_NAME = {
    constraints: 'constraints.json',
    rules:       'rules_annotations.json',
    twinmaker:   'twinmaker_ids.json',
    models:      'model_files.json',
  };

  // ── Save section to DynamoDB via API ─────────────────────────────────────────
  const handleSave = async (sec) => {
    const payload = buildPayload(sec);
    if (!payload) return;
    if (!email.trim()) {
      setSaveStatus(prev => ({ ...prev, [sec]: 'error' }));
      setError('Enter your email address before saving.');
      return;
    }
    setSaveStatus(prev => ({ ...prev, [sec]: 'saving' }));
    try {
      await axios.put(`${API_BASE}/api/config`, {
        email:     email.trim().toLowerCase(),
        configKey: FILE_NAME[sec],
        data:      payload,
      });
      setSaveStatus(prev => ({ ...prev, [sec]: 'saved' }));
      setTimeout(() => setSaveStatus(prev => ({ ...prev, [sec]: null })), 3000);
    } catch {
      setSaveStatus(prev => ({ ...prev, [sec]: 'error' }));
    }
  };

  // ── Download section as JSON file ─────────────────────────────────────────
  const handleDownload = (sec) => {
    const payload = buildPayload(sec);
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = FILE_NAME[sec];
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Section toolbar (Save + Download) ────────────────────────────────────
  const SectionToolbar = ({ sec }) => {
    const st = saveStatus[sec];
    return (
      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <button onClick={() => handleSave(sec)} disabled={st === 'saving'}
          style={{ ...s.saveBtn, opacity: st === 'saving' ? 0.6 : 1 }}>
          {st === 'saving' ? 'Saving…' : '💾 Save'}
        </button>
        <button onClick={() => handleDownload(sec)} style={s.dlBtn}>
          ⬇ Download JSON
        </button>
        {st === 'saved' && <span style={{ color: '#27ae60', fontSize: 13 }}>✅ Saved</span>}
        {st === 'error' && <span style={{ color: '#c00',     fontSize: 13 }}>❌ Save failed</span>}
      </div>
    );
  };

  // ── Constraints helpers ───────────────────────────────────────────────────
  const updateGeneral = (i, field, val) =>
    setConstraints(prev => ({
      ...prev,
      general: prev.general.map((r, idx) => idx === i ? { ...r, [field]: val } : r),
    }));

  const addGeneral = () =>
    setConstraints(prev => ({
      ...prev,
      general: [...prev.general, { name: '', label: '', value: 0, unit: '' }],
    }));

  const removeGeneral = (i) =>
    setConstraints(prev => ({ ...prev, general: prev.general.filter((_, idx) => idx !== i) }));

  // ── Rules table ─────────────────────────────────────────────────────────────
  const updateRule = (i, field, val) =>
    setRules(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const addRule = () => setRules(prev => [...prev, {
    id: `R_NEW_${String(prev.length + 1).padStart(3, '0')}`,
    category: 'Sequence', severity: 'Major', description: '',
  }]);

  const removeRule = (i) => setRules(prev => prev.filter((_, idx) => idx !== i));

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!version.trim())                          return setError('Version label is required (e.g. v2.0)');
    if (!unityFiles.scene)                        return setError('BIW_RealVirtual_Scene.unity is required');
    if (!unityFiles.exporter)                     return setError('BIW_EventExporter.cs is required');
    if (!unityFiles.bridge)                       return setError('BIW_AWS_Bridge.cs is required');

    setSubmitting(true);
    setError(null);

    try {
      // Build the 4 config JSON files from form state
      // general constraints serialised back to flat key:value object (matching constraints.json format)
      const generalObj = Object.fromEntries(
        constraints.general.filter(g => g.name).map(g => [g.name, g.value])
      );
      const constraintsJson = JSON.stringify({
        ...generalObj,
        timing: constraints.timing,
      });

      const rulesJson = JSON.stringify(
        rules.map(({ id, category, severity, description }) => ({ id, category, severity, description }))
      );

      const tmIdsJson = JSON.stringify(
        Object.fromEntries(tmIds.map(r => [r.device, r.entity]))
      );

      const modelFilesJson = JSON.stringify(
        Object.fromEntries(modelFiles.map(r => [r.device, r.file]))
      );

      // Convert JSON strings to File objects for upload
      const toFile = (content, name) =>
        new File([content], name, { type: 'application/json' });

      const allFiles = [
        unityFiles.scene,
        unityFiles.exporter,
        unityFiles.bridge,
        toFile(constraintsJson,  'constraints.json'),
        toFile(rulesJson,        'rules_annotations.json'),
        toFile(tmIdsJson,        'twinmaker_ids.json'),
        toFile(modelFilesJson,   'model_files.json'),
      ];

      // Step 1: get pre-signed URLs
      const { data: { jobId, uploadUrls } } = await axios.post(`${API_BASE}/api/upload`, {
        files:         allFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
        designVersion: version.trim(),
        baselineJobId: baseline.trim() || undefined,
      });

      // Step 2: PUT all files to S3
      await Promise.all(allFiles.map((file, i) =>
        axios.put(uploadUrls[i], file, { headers: { 'Content-Type': file.type } })
      ));

      // Step 3: trigger pipeline
      await axios.post(`${API_BASE}/api/jobs/${jobId}/start`);

      onJobCreated(jobId);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const allUnityReady = unityFiles.scene && unityFiles.exporter && unityFiles.bridge;

  return (
    <div style={s.page}>

      {/* Version + Baseline */}
      <div style={s.versionBar}>
        <div style={s.fieldRow}>
          <span style={{ fontSize: 13, color: '#666' }}>Signed in as <strong>{email}</strong></span>
        </div>
        <div style={s.fieldRow}>
          <label style={{ ...s.label, fontWeight: 700 }}>Design Version <span style={{ color: '#c00' }}>*</span></label>
          <input style={{ ...s.input, width: 120 }} placeholder="e.g. v2.0"
            value={version} onChange={e => setVersion(e.target.value)} />
        </div>
        <div style={s.fieldRow}>
          <label style={s.label}>Compare against baseline</label>
          <input style={{ ...s.input, width: 180 }} placeholder="Job ID of v1.0 (optional)"
            value={baseline} onChange={e => setBaseline(e.target.value)} />
          <small style={{ color: '#888', marginLeft: 6 }}>Leave blank for first submission</small>
        </div>
      </div>

      {/* Section nav */}
      <div style={s.secNav}>
        {[
          ['unity',       `🗂 Unity Files ${allUnityReady ? '✅' : '⚠️'}`],
          ['constraints', '⚙️ Constraints'],
          ['rules',       `📋 Rules (${rules.length})`],
          ['twinmaker',   '🔗 TwinMaker IDs'],
          ['models',      '📦 Model Files'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setSection(key)}
            style={{ ...s.secBtn, borderBottom: section === key ? '3px solid #0073bb' : '3px solid transparent', fontWeight: section === key ? 700 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Section: Unity Files ── */}
      {section === 'unity' && (
        <div style={s.card}>
          <p style={s.hint}>Upload the 3 files directly from your Unity project. These are parsed automatically by the KG generator.</p>
          {UNITY_SLOTS.map(({ key: slotKey, label, hint, ext }) => (
            <UnitySlot key={slotKey} slotKey={slotKey} label={label} hint={hint} ext={ext} />
          ))}
        </div>
      )}

      {/* ── Section: Constraints ── */}
      {section === 'constraints' && (
        <div style={s.card}>
          <p style={s.hint}>Pre-filled from <code>inputs-kg/constraints.json</code>. Edit only if your design changes these thresholds.</p>

          <h4 style={{ margin: '0 0 8px' }}>General Constraints</h4>
          <table style={s.table}>
            <thead><tr>
              <Th>Parameter Name</Th><Th>Label</Th><Th>Value</Th><Th>Unit</Th><Th></Th>
            </tr></thead>
            <tbody>
              {constraints.general.map((g, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <Td>
                    <input style={{ ...s.input, width: 200, fontFamily: 'monospace', fontSize: 12 }}
                      value={g.name} placeholder="e.g. ClampForceMinN"
                      onChange={e => updateGeneral(i, 'name', e.target.value)} />
                  </Td>
                  <Td>
                    <input style={{ ...s.input, width: 180 }}
                      value={g.label} placeholder="e.g. Clamp Force Min"
                      onChange={e => updateGeneral(i, 'label', e.target.value)} />
                  </Td>
                  <Td>
                    <input type="number" step="any" style={{ ...s.input, width: 90 }}
                      value={g.value}
                      onChange={e => updateGeneral(i, 'value', parseFloat(e.target.value) || 0)} />
                  </Td>
                  <Td>
                    <input style={{ ...s.input, width: 60 }}
                      value={g.unit} placeholder="N"
                      onChange={e => updateGeneral(i, 'unit', e.target.value)} />
                  </Td>
                  <Td>
                    <button onClick={() => removeGeneral(i)} style={s.removeBtn} title="Remove">✕</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addGeneral} style={s.addBtn}>+ Add General Constraint</button>

          <h4 style={{ margin: '20px 0 8px' }}>Timing Constraints</h4>
          <table style={s.table}>
            <thead><tr>
              <Th>From Event</Th><Th>To Event</Th><Th>Max Duration (s)</Th><Th></Th>
            </tr></thead>
            <tbody>
              {constraints.timing.map((t, i) => (
                <tr key={i}>
                  <Td>
                    <input style={{ ...s.input, width: 180 }} value={t.event}
                      onChange={e => setConstraints(prev => ({
                        ...prev,
                        timing: prev.timing.map((row, idx) => idx === i ? { ...row, event: e.target.value } : row)
                      }))} />
                  </Td>
                  <Td>
                    <input style={{ ...s.input, width: 180 }} value={t.to_event}
                      onChange={e => setConstraints(prev => ({
                        ...prev,
                        timing: prev.timing.map((row, idx) => idx === i ? { ...row, to_event: e.target.value } : row)
                      }))} />
                  </Td>
                  <Td>
                    <input type="number" step="0.1" style={{ ...s.input, width: 80 }}
                      value={t.max_duration_s}
                      onChange={e => setConstraints(prev => ({
                        ...prev,
                        timing: prev.timing.map((row, idx) => idx === i ? { ...row, max_duration_s: parseFloat(e.target.value) || 0 } : row)
                      }))} />
                  </Td>
                  <Td>
                    <button onClick={() => setConstraints(prev => ({ ...prev, timing: prev.timing.filter((_, idx) => idx !== i) }))}
                      style={s.removeBtn} title="Remove">✕</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => setConstraints(prev => ({
            ...prev,
            timing: [...prev.timing, { event: '', to_event: '', max_duration_s: 0 }]
          }))} style={s.addBtn}>+ Add Timing Constraint</button>
          <SectionToolbar sec="constraints" />
        </div>
      )}

      {/* ── Section: Rules ── */}
      {section === 'rules' && (
        <div style={s.card}>
          <p style={s.hint}>Pre-filled from <code>inputs-kg/rules_annotations.json</code>. Edit descriptions/severity or add new rules for this design version.</p>
          <table style={s.table}>
            <thead><tr>
              <Th>Rule ID</Th><Th>Category</Th><Th>Severity</Th><Th>Description</Th><Th></Th>
            </tr></thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <Td>
                    <input style={{ ...s.input, width: 110, fontFamily: 'monospace', fontSize: 12 }}
                      value={r.id} onChange={e => updateRule(i, 'id', e.target.value)} />
                  </Td>
                  <Td>
                    <select style={s.select} value={r.category} onChange={e => updateRule(i, 'category', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </Td>
                  <Td>
                    <select style={{ ...s.select, color: SEV_COLOR[r.severity] || '#333', fontWeight: 600 }}
                      value={r.severity} onChange={e => updateRule(i, 'severity', e.target.value)}>
                      {SEVERITIES.map(sv => <option key={sv}>{sv}</option>)}
                    </select>
                  </Td>
                  <Td>
                    <input style={{ ...s.input, width: '100%', minWidth: 260 }}
                      value={r.description} onChange={e => updateRule(i, 'description', e.target.value)} />
                  </Td>
                  <Td>
                    <button onClick={() => removeRule(i)} style={s.removeBtn} title="Remove rule">✕</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addRule} style={s.addBtn}>+ Add Rule</button>
          <SectionToolbar sec="rules" />
        </div>
      )}

      {/* ── Section: TwinMaker IDs ── */}
      {section === 'twinmaker' && (
        <div style={s.card}>
          <p style={s.hint}>Pre-filled from <code>inputs-kg/twinmaker_ids.json</code>. Maps KG device IDs to TwinMaker entity names in your workspace.</p>
          <table style={s.table}>
            <thead><tr><Th>Device ID</Th><Th>TwinMaker Entity Name</Th><Th></Th></tr></thead>
            <tbody>
              {tmIds.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <Td>
                    <input style={{ ...s.input, width: 200, fontFamily: 'monospace', fontSize: 12 }}
                      value={row.device} placeholder="e.g. Fixture_001"
                      onChange={e => setTmIds(prev => prev.map((r, idx) => idx === i ? { ...r, device: e.target.value } : r))} />
                  </Td>
                  <Td>
                    <input style={{ ...s.input, width: 220 }}
                      value={row.entity} placeholder="e.g. Fixture_001"
                      onChange={e => setTmIds(prev => prev.map((r, idx) => idx === i ? { ...r, entity: e.target.value } : r))} />
                  </Td>
                  <Td>
                    <button onClick={() => setTmIds(prev => prev.filter((_, idx) => idx !== i))} style={s.removeBtn} title="Remove">✕</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => setTmIds(prev => [...prev, { device: '', entity: '' }])} style={s.addBtn}>+ Add TwinMaker ID</button>
          <SectionToolbar sec="twinmaker" />
        </div>
      )}

      {/* ── Section: Model Files ── */}
      {section === 'models' && (
        <div style={s.card}>
          <p style={s.hint}>Pre-filled from <code>inputs-kg/model_files.json</code>. Maps device IDs to FBX file names used in the KG.</p>
          <table style={s.table}>
            <thead><tr><Th>Device ID</Th><Th>FBX File Name</Th><Th></Th></tr></thead>
            <tbody>
              {modelFiles.map((row, i) => (
                <tr key={i}>
                  <Td>
                    <input style={{ ...s.input, width: 140 }} value={row.device}
                      onChange={e => setModelFiles(prev => prev.map((r, idx) => idx === i ? { ...r, device: e.target.value } : r))} />
                  </Td>
                  <Td>
                    <input style={{ ...s.input, width: 180 }} value={row.file}
                      onChange={e => setModelFiles(prev => prev.map((r, idx) => idx === i ? { ...r, file: e.target.value } : r))} />
                  </Td>
                  <Td>
                    <button onClick={() => setModelFiles(prev => prev.filter((_, idx) => idx !== i))} style={s.removeBtn}>✕</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => setModelFiles(prev => [...prev, { device: '', file: '' }])} style={s.addBtn}>+ Add Model</button>
          <SectionToolbar sec="models" />
        </div>
      )}

      {error && <p style={s.error}>{error}</p>}

      {/* Submit */}
      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={handleSubmit} disabled={submitting}
          style={{ ...s.submitBtn, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Submitting…' : '🚀 Submit for Design Validation'}
        </button>
        {!allUnityReady && <span style={{ color: '#e67e22', fontSize: 13 }}>⚠️ Upload all 3 Unity files first</span>}
        {!version.trim()  && <span style={{ color: '#e67e22', fontSize: 13 }}>⚠️ Version label required</span>}
      </div>


    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
const Th = ({ children }) => <th style={{ textAlign: 'left', padding: '7px 10px', background: '#f0f7ff', borderBottom: '2px solid #cce', fontSize: 12, whiteSpace: 'nowrap' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', verticalAlign: 'middle' }}>{children}</td>;

const s = {
  page:       { fontFamily: 'sans-serif', maxWidth: 960, margin: '0 auto' },
  versionBar: { display: 'flex', gap: 32, alignItems: 'flex-end', background: '#fffbe6', border: '1px solid #f0c040', borderRadius: 6, padding: '12px 16px', marginBottom: 20, flexWrap: 'wrap' },
  secNav:     { display: 'flex', borderBottom: '1px solid #ddd', marginBottom: 16, flexWrap: 'wrap' },
  secBtn:     { padding: '9px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 },
  card:       { background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 18, marginBottom: 12 },
  hint:       { color: '#666', fontSize: 13, margin: '0 0 12px 0' },
  formGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' },
  input:      { padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13, width: 120 },
  select:     { padding: '5px 6px', border: '1px solid #ccc', borderRadius: 4, fontSize: 12 },
  table:      { width: '100%', borderCollapse: 'collapse' },
  code:       { background: '#f4f4f4', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: 12 },
  slotRow:    { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0' },
  slotLabel:  { minWidth: 280, flexShrink: 0 },
  slotFilled: { display: 'flex', alignItems: 'center', gap: 10, background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: 6, padding: '6px 12px', fontSize: 13 },
  miniDrop:   { border: '2px dashed', borderRadius: 6, padding: '8px 20px', cursor: 'pointer', background: '#fafafa', fontSize: 12 },
  removeBtn:  { background: 'none', border: 'none', cursor: 'pointer', color: '#c00', fontSize: 14 },
  saveBtn:    { padding: '6px 14px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  dlBtn:      { padding: '6px 14px', background: '#f0f7ff', border: '1px solid #0073bb', color: '#0073bb', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  addBtn:     { marginTop: 10, padding: '6px 14px', background: '#f0f7ff', border: '1px solid #0073bb', color: '#0073bb', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  submitBtn:  { padding: '11px 28px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 15, fontWeight: 600 },
  error:      { color: '#c00', marginTop: 10, fontSize: 13 },
};
